// @ts-check
/**
 * Sales events: the pop-ups and other places staff sell in person. Each in-person sale is recorded
 * against one. Archive hides an event from the sale form and keeps its history; delete only works
 * while no sale has been recorded against it.
 */
import { Archive, ArchiveRestore, Pencil, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import CountrySelect from '../components/CountrySelect';
import LoadingScreen from '../components/LoadingScreen';
import WalkInLeadsPanel from '../components/WalkInLeadsPanel';
import Modal from '../components/ui/Modal';
import { useToast } from '../context/ToastContext';
import adminSalesEventService from '../services/adminSalesEventService';
import { GST_STATE_NAMES } from '../utils/gstStates';
import { formatEventDates, todayInIndia } from '../utils/salesEvents';

/** @typedef {import('../services/adminSalesEventService').SalesEvent} SalesEvent */

const input = 'w-full px-3 py-2 text-base border border-input rounded-lg';

const blank = () => ({
  name: '',
  venue: '',
  country_code: '',
  state: '',
  starts_on: todayInIndia(),
  ends_on: '',
  notes: '',
});

/** @param {{event: SalesEvent | null, onClose: () => void, onSaved: () => void}} props */
const EventFormModal = ({ event, onClose, onSaved }) => {
  const toast = useToast();
  const [form, setForm] = useState(() =>
    event
      ? {
          name: event.name,
          venue: event.venue || '',
          country_code: event.country_code || '',
          state: event.state || '',
          starts_on: event.starts_on,
          ends_on: event.ends_on || '',
          notes: event.notes || '',
        }
      : blank(),
  );
  const [saving, setSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  const set = (patch) => {
    setIsDirty(true);
    setForm((prev) => ({ ...prev, ...patch }));
  };

  const handleClose = () => {
    if (isDirty && !window.confirm('Discard your changes?')) return;
    onClose();
  };

  const handleSave = async () => {
    if (!form.name.trim()) return toast.error('Enter the event name');
    if (!form.country_code) return toast.error('Choose the country the event is in');
    if (!form.starts_on) return toast.error('Enter the start date');
    if (form.ends_on && form.ends_on < form.starts_on)
      return toast.error('The end date cannot be before the start date');

    const body = {
      name: form.name.trim(),
      venue: form.venue.trim() || null,
      country_code: form.country_code,
      state: form.state.trim() || null,
      starts_on: form.starts_on,
      ends_on: form.ends_on || null,
      notes: form.notes.trim() || null,
    };
    setSaving(true);
    try {
      if (event) await adminSalesEventService.update(event.id, body);
      else await adminSalesEventService.create(body);
      toast.success(event ? 'Event updated' : 'Event added');
      setIsDirty(false);
      onSaved();
    } catch (err) {
      toast.error(err.message || 'Failed to save the event');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal maxWidth="md" onClose={handleClose} hasUnsavedChanges={isDirty}>
      <div className="px-6 py-4 border-b">
        <h2 className="text-lg font-semibold">{event ? 'Edit event' : 'Add event'}</h2>
      </div>
      <div className="px-6 py-4 space-y-3 max-h-[65vh] overflow-y-auto">
        <div>
          <label className="block text-sm font-medium mb-1">Name *</label>
          <input
            className={input}
            placeholder="e.g. Soho House pop-up"
            value={form.name}
            onChange={(e) => set({ name: e.target.value })}
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Venue</label>
          <input
            className={input}
            placeholder="e.g. Soho House, Juhu, Mumbai"
            value={form.venue}
            onChange={(e) => set({ venue: e.target.value })}
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Country *</label>
          <CountrySelect
            variant="full"
            ariaLabel="Country"
            value={form.country_code}
            onChange={(country_code) => set({ country_code })}
          />
          <p className="text-xs text-muted-foreground mt-1">
            Sets the default country for phone numbers captured at this event.
          </p>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">State</label>
          <select className={input} value={form.state} onChange={(e) => set({ state: e.target.value })}>
            <option value="">Choose the state...</option>
            {GST_STATE_NAMES.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
          <p className="text-xs text-muted-foreground mt-1">
            Needed for the GST tax invoice on a sale handed over on the spot at this event.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium mb-1">Starts *</label>
            <input
              type="date"
              className={input}
              value={form.starts_on}
              onChange={(e) => set({ starts_on: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Ends</label>
            <input
              type="date"
              className={input}
              value={form.ends_on}
              min={form.starts_on}
              onChange={(e) => set({ ends_on: e.target.value })}
            />
          </div>
        </div>
        <p className="text-xs text-muted-foreground">Leave the end date empty for a one-day event.</p>
        <div>
          <label className="block text-sm font-medium mb-1">Notes</label>
          <textarea className={input} rows={3} value={form.notes} onChange={(e) => set({ notes: e.target.value })} />
        </div>
      </div>
      <div className="flex gap-2 px-6 py-4 border-t">
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-4 py-2 text-sm bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:bg-muted-foreground/50"
        >
          {saving ? 'Saving...' : 'Save'}
        </button>
        <button
          onClick={handleClose}
          disabled={saving}
          className="px-4 py-2 text-sm border border-input rounded-lg hover:bg-accent"
        >
          Cancel
        </button>
      </div>
    </Modal>
  );
};

const SalesEventsPage = () => {
  const toast = useToast();
  /** @type {[SalesEvent[], Function]} */
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false);
  /** @type {[SalesEvent | null | undefined, Function]} undefined = form closed, null = adding, event = editing */
  const [editing, setEditing] = useState(undefined);
  const [tab, setTab] = useState('events');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setEvents(await adminSalesEventService.list({ includeArchived: showArchived }));
    } catch (err) {
      toast.error(err.message || 'Failed to load events');
    } finally {
      setLoading(false);
    }
  }, [showArchived, toast]);

  useEffect(() => {
    load();
  }, [load]);

  /** @param {SalesEvent} event */
  const toggleArchive = async (event) => {
    try {
      await adminSalesEventService.update(event.id, { is_active: !event.is_active });
      toast.success(event.is_active ? 'Event archived' : 'Event restored');
      load();
    } catch (err) {
      toast.error(err.message || 'Failed to update the event');
    }
  };

  /** @param {SalesEvent} event */
  const handleDelete = async (event) => {
    if (!window.confirm(`Delete "${event.name}"? This cannot be undone.`)) return;
    try {
      await adminSalesEventService.remove(event.id);
      toast.success('Event deleted');
      load();
    } catch (err) {
      toast.error(err.message || 'Failed to delete the event');
    }
  };

  return (
    <div>
      <div className="flex flex-wrap justify-between items-center gap-3 mb-6">
        <h1 className="text-3xl font-serif">Sales Events</h1>
        <div className="flex items-center gap-3">
          {tab === 'events' && (
            <>
              <label className="flex items-center gap-2 text-sm text-muted-foreground">
                <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />
                Show archived
              </label>
              <button
                onClick={load}
                disabled={loading}
                className="flex items-center gap-2 px-4 py-2 border rounded-lg border-input hover:bg-muted/50 disabled:opacity-50"
              >
                <RefreshCw size={18} className={loading ? 'animate-spin' : ''} /> Refresh
              </button>
              <button
                onClick={() => setEditing(null)}
                className="flex items-center gap-2 px-4 py-2 text-sm bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
              >
                <Plus size={16} /> Add event
              </button>
            </>
          )}
        </div>
      </div>

      <div className="flex gap-1 bg-muted rounded-lg p-1 mb-6 max-w-xs overflow-x-auto">
        {[
          { key: 'events', label: 'Events' },
          { key: 'leads', label: 'Leads' },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`flex-shrink-0 whitespace-nowrap flex-1 px-4 py-2 text-sm font-medium rounded-lg transition ${
              tab === t.key ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'leads' && <WalkInLeadsPanel events={events} />}

      {tab === 'events' &&
        (loading ? (
          <LoadingScreen />
        ) : events.length === 0 ? (
          <div className="bg-card border rounded-lg border-border p-12 text-center text-muted-foreground">
            No events yet. Add the first one, then choose it when you record an in-person sale.
          </div>
        ) : (
          <div className="bg-card border rounded-lg border-border shadow-sm overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50 border-b border-border">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Event</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Dates</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase">Sales</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-muted-foreground uppercase">Status</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-muted-foreground uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {events.map((event) => (
                  <tr
                    key={event.id}
                    className={event.is_active ? 'hover:bg-muted/50' : 'bg-muted/50 text-muted-foreground'}
                  >
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium">{event.name}</div>
                      {event.venue && <div className="text-xs text-muted-foreground">{event.venue}</div>}
                    </td>
                    <td className="px-6 py-4 text-sm">{formatEventDates(event)}</td>
                    <td className="px-6 py-4 text-sm text-right">{event.sale_count}</td>
                    <td className="px-6 py-4 text-center">
                      <span
                        className={`inline-flex px-3 py-1 rounded-md text-xs font-medium ${event.is_active ? 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300' : 'bg-accent text-foreground/80'}`}
                      >
                        {event.is_active ? 'Active' : 'Archived'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-center gap-3">
                        <button
                          onClick={() => setEditing(event)}
                          className="text-muted-foreground hover:text-foreground"
                          title="Edit"
                          aria-label={`Edit ${event.name}`}
                        >
                          <Pencil size={18} />
                        </button>
                        <button
                          onClick={() => toggleArchive(event)}
                          className="text-muted-foreground hover:text-foreground"
                          title={event.is_active ? 'Archive (hide from the sale form)' : 'Restore'}
                          aria-label={`${event.is_active ? 'Archive' : 'Restore'} ${event.name}`}
                        >
                          {event.is_active ? <Archive size={18} /> : <ArchiveRestore size={18} />}
                        </button>
                        <button
                          onClick={() => handleDelete(event)}
                          disabled={event.sale_count > 0}
                          className="text-muted-foreground hover:text-red-600 dark:hover:text-red-400 disabled:opacity-30 disabled:hover:text-foreground"
                          title={event.sale_count > 0 ? `Has ${event.sale_count} sales. Archive it instead.` : 'Delete'}
                          aria-label={`Delete ${event.name}`}
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}

      {editing !== undefined && (
        <EventFormModal
          event={editing}
          onClose={() => setEditing(undefined)}
          onSaved={() => {
            setEditing(undefined);
            load();
          }}
        />
      )}
    </div>
  );
};

export default SalesEventsPage;
